import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Container from './Container';
import { FuturisticButton } from '../ui/futuristic';
import CurrencySelector from '../ui/CurrencySelector';
import Logo from '../ui/Logo';

const navItems = [
  { label: 'Home', path: '/' },
  {
    label: 'Services',
    path: '/services',
    submenu: [
      { label: 'AI Training', path: '/services/ai-training' },
      { label: 'AI Onboarding', path: '/services/ai-onboarding' },
      { label: 'Workflow Automation', path: '/services/workflow-automation' },
      { label: 'CRM & Pipeline', path: '/services/crm-pipeline-automation' },
      { label: 'Custom AI Dev', path: '/services/custom-ai-development' },
      { label: 'AI Optimisation', path: '/services/ai-optimization' },
      { label: 'Web Design', path: '/services/web-design' },
      { label: 'Software Dev', path: '/services/software-development' },
      { label: 'SEO', path: '/services/seo' },
      { label: 'Digital Marketing', path: '/services/digital-marketing' },
    ],
  },
  {
    label: 'Tools',
    path: '/tools',
    submenu: [
      { label: 'AI Service Selector', path: '/tools/service-selector' },
      { label: 'Pipeline ROI Calculator', path: '/tools/pipeline-roi-calculator' },
    ],
  },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

const FuturisticHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSubmenuClick = (index: number) => {
    setActiveSubmenu(activeSubmenu === index ? null : index);
  };

  return (
    <header
      className={[
        'fixed w-full z-30 transition-all duration-300 bg-neu-bg',
        scrolled
          ? 'py-2 shadow-[0_4px_16px_rgb(163,177,198,0.5),-0px_-4px_16px_rgba(255,255,255,0.4)]'
          : 'py-3',
      ].join(' ')}
    >
      <Container>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Logo size="sm" />

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <ul className="flex space-x-1">
              {navItems.map((item, idx) => (
                <li key={idx} className="relative group">
                  {item.submenu ? (
                    <div className="flex items-center cursor-pointer px-3 py-2 rounded-neu-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 font-display font-medium text-sm">
                      <span>{item.label}</span>
                      <svg
                        className="h-4 w-4 ml-1 transition-transform duration-200 group-hover:rotate-180"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className="block px-3 py-2 rounded-neu-sm text-neu-muted hover:text-neu-accent transition-colors duration-200 font-display font-medium text-sm no-underline"
                    >
                      {item.label}
                    </Link>
                  )}

                  {item.submenu && (
                    <div className={`absolute left-0 mt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${item.submenu.length > 5 ? 'w-80' : 'w-52'}`}>
                      <div
                        className="bg-neu-bg rounded-neu-sm overflow-hidden"
                        style={{
                          boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)',
                        }}
                      >
                        <div className={`py-2 ${item.submenu.length > 5 ? 'grid grid-cols-2' : ''}`}>
                          {item.submenu.map((subItem, subIdx) => (
                            <Link
                              key={subIdx}
                              to={subItem.path}
                              className="block px-4 py-2 text-sm text-neu-muted hover:text-neu-accent hover:bg-white/30 transition-colors duration-200 no-underline font-display"
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="flex items-center space-x-3">
              <CurrencySelector variant="minimal" />
              <FuturisticButton
                variant="accent"
                size="sm"
                onClick={() => (window.location.href = '/book')}
              >
                Book a Free Call
              </FuturisticButton>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              type="button"
              className={[
                'inline-flex items-center justify-center w-11 h-11 rounded-neu-sm bg-neu-bg text-neu-muted',
                'shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
                'active:shadow-[inset_4px_4px_8px_rgb(163,177,198,0.6),inset_-4px_-4px_8px_rgba(255,255,255,0.5)]',
                'transition-all duration-200',
              ].join(' ')}
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile menu */}
      <div className={`lg:hidden ${isMenuOpen ? 'block' : 'hidden'}`}>
        <div
          className="mx-4 mt-2 mb-3 rounded-neu bg-neu-bg px-4 py-4"
          style={{
            boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)',
          }}
        >
          {navItems.map((item, idx) => (
            <div key={idx}>
              {item.submenu ? (
                <div>
                  <button
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-display font-medium text-neu-muted hover:text-neu-accent rounded-neu-sm transition-colors duration-200"
                    onClick={() => handleSubmenuClick(idx)}
                  >
                    <span>{item.label}</span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${activeSubmenu === idx ? 'rotate-180' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {activeSubmenu === idx && (
                    <div className="pl-4 py-1 space-y-0.5">
                      {item.submenu.map((subItem, subIdx) => (
                        <Link
                          key={subIdx}
                          to={subItem.path}
                          className="block px-3 py-2 text-sm font-display text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline rounded-neu-sm"
                          onClick={toggleMenu}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  className="block px-3 py-2.5 text-sm font-display font-medium text-neu-muted hover:text-neu-accent transition-colors duration-200 no-underline rounded-neu-sm"
                  onClick={toggleMenu}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <div className="pt-3 mt-3 border-t border-white/30 space-y-3">
            <div className="flex items-center justify-between px-3 py-1">
              <span className="text-sm font-display font-medium text-neu-muted">Currency</span>
              <CurrencySelector variant="minimal" />
            </div>
            <FuturisticButton
              variant="accent"
              fullWidth
              onClick={() => (window.location.href = '/book')}
            >
              Book a Free Call
            </FuturisticButton>
          </div>
        </div>
      </div>
    </header>
  );
};

export default FuturisticHeader;
